
import GpuProgram from './program';
import GpuTexture from './texture';
import Renderer from '../renderer';
import type EventBus from '../../map/event-bus';
import type Map from '../../map/map';
import * as utils from '../../utils/utils';


/**
 * GpuDevice, despite its name, is not an abstraction of a GPU device.
 * Here is what it does:
 *
 *   * it manages the canvas inside the map element and sets its size properly
 *     according to the provided CSS pixel size and device pixel ratio.
 *
 *   * it tracks the current render target and keeps framebuffer binding and the
 *     gl viewport in sync with it
 *
 *   * it abstracts various GL rendering flags (gl.BLEND, gl.STENCIL_TEST,
 *     gl.DEPTH) into a single state object, with getState and setState accessors.
 *
 *   * it provides a thin wrapper around gl.useProgram and gl.bindTexture
 *
 *   * it reads pixels from framebuffer-backed textures without exposing raw
 *     framebuffer binding as a public rendering operation
 */

export class GpuDevice {

    /**
     * Renderer that owns this WebGL context.
     */
    renderer!: Renderer;

    /**
     * Event bus owned by the typed `Map`; context-loss events publish
     * through it.
     */
    bus!: EventBus<Map.ViewerEventMap>;

    /**
     * DOM container that receives the managed canvas.
     */
    div!: HTMLElement;

    /**
     * Canvas element whose default framebuffer is the base render target.
     */
    canvas!: HTMLCanvasElement;

    /**
     * WebGL2 context used by all renderer GPU objects.
     */
    gl!: WebGL2RenderingContext;

    /**
     * Current drawing destination. All draw-target changes should go through
     * `setRenderTarget()` so framebuffer and viewport state stay in sync.
     */
    private renderTarget_!: GpuDevice.RenderTarget;

    /**
     * Last configured canvas render target. Updated only by
     * `updateCanvasRenderTarget()`. Used by `canvasRenderTargetNeedsUpdate()`
     * and `setCanvasRenderTarget()` independently of which target is
     * currently bound.
     */
    private canvasTarget_!: GpuDevice.RenderTarget;

    /**
     * Cached WebGL fixed-function state managed by `setState()`.
     */
    currentState!: GpuDevice.State;

    /**
     * Currently bound GPU program, used to skip redundant gl.useProgram calls.
     */
    currentProgram?: GpuProgram;

    /**
     * Whether the default framebuffer should preserve contents for screenshots.
     */
    keepFrameBuffer!: boolean;

    /**
     * Whether the WebGL context was requested with antialiasing enabled.
     */
    antialias!: boolean;

    /**
     * Requested anisotropic filtering level, clamped to the device maximum.
     */
    anisoLevel!: GLfloat;

    /**
     * Maximum anisotropic filtering level reported by the browser.
     */
    maxAniso!: GLfloat;

    /**
     * Optional anisotropic filtering extension.
     */
    anisoExt?: EXT_texture_filter_anisotropic | null;

    /**
     * @deprecated Used only by legacy `useProgram()` attribute toggling.
     */
    maxAttributesCount = 8;

    /**
     * @deprecated Scratch buffer used only by legacy `useProgram()`.
     */
    newAttributes = new Uint8Array(this.maxAttributesCount);

    /**
     * @deprecated Attribute-enable cache used only by legacy `useProgram()`.
     */
    enabledAttributes = new Uint8Array(this.maxAttributesCount);

    private disposed_ = false;

    /**
     * Cumulative GL draw calls, render texture binds, and draw-framebuffer
     * switches. `FrameProfiler` reads per-frame deltas of these; they are
     * never reset here. The draw count is incremented by the wrappers
     * installed in `installDrawCounters`; the texture bind count by
     * `bindTexture`; the switch count by `bindFramebuffer` when the bound
     * framebuffer changes.
     */
    drawCallCount = 0;
    textureBindCount = 0;
    fboSwitchCount = 0;

    /** Last framebuffer bound per target, so only real switches are counted. */
    private boundFramebuffer_ =
        new globalThis.Map<GLenum, WebGLFramebuffer | null>();

/**
 * Create the WebGL canvas/context wrapper for a renderer.
 *
 * @param renderer Renderer that owns this device.
 * @param div DOM container that receives the created canvas.
 * @param keepFrameBuffer Whether the canvas should preserve drawing buffer
 * contents for screenshots.
 * @param antialias Whether to request an antialiased WebGL context.
 * @param aniso Requested anisotropic filtering level.
 * @param bus Event bus for the context-loss events.
 */
constructor(
    renderer: Renderer,
    div: HTMLElement,
    keepFrameBuffer: boolean,
    antialias: boolean,
    aniso: GLfloat,
    bus: EventBus<Map.ViewerEventMap>
) {

    this.renderer = renderer;
    this.bus = bus;
    this.div = div;
    this.keepFrameBuffer = keepFrameBuffer;
    this.antialias = antialias;
    this.anisoLevel = aniso;

    // canvas ownership.
    const canvas = document.createElement('canvas');
    this.canvas = canvas;
    canvas.style.display = 'block';
    this.div.appendChild(canvas);

    canvas.addEventListener(
        "webglcontextlost",
        this.contextLost.bind(this),
        false,
    );
    canvas.addEventListener(
        "webglcontextrestored",
        this.contextRestored.bind(this),
        false,
    );

    // WebGL context.
    const context = canvas.getContext('webgl2', {
        preserveDrawingBuffer: this.keepFrameBuffer,
        antialias: this.antialias,
        stencil: true,
    });

    if (!context) throw new Error('cartolina-js requires WebGL2.');

    const gl = this.gl = context;

    // Count every draw call centrally. Wrapping the context's draw
    // functions once catches terrain, mask, and geodata draws alike; the
    // increment is trivial next to the GL work itself.
    this.installDrawCounters(gl);

    // device capabilities.
    this.anisoExt = gl.getExtension('EXT_texture_filter_anisotropic');

    if (this.anisoExt) {
        this.maxAniso = gl.getParameter(
            this.anisoExt.MAX_TEXTURE_MAX_ANISOTROPY_EXT,
        );

        if (this.anisoLevel) {
            if (this.anisoLevel == -1) {
                this.anisoLevel = this.maxAniso;
            } else {
                this.anisoLevel = Math.min(this.anisoLevel, this.maxAniso);
            }
        }
    } else {
        this.maxAniso = 0;
        this.anisoLevel = 0;
    }

    // initial render target.
    this.renderTarget_ = {
        kind: 'canvas',
        viewportSize: [canvas.width, canvas.height],
        apparentSize: [canvas.width, canvas.height],
    };
    this.canvasTarget_ = this.renderTarget_;

    // initial renderer state.
    this.currentState = {
        blend: false,
        stencil: false,
        zwrite: false,
        ztest: true,
        zequal: false,
        culling: false,
    };
    this.setState(this.currentState, true);

    // Initial clear.
    this.resetViewport();
    this.clearColorAndDepth();
};


/**
 * Verifies that the browser can create a WebGL2 context.
 *
 * @throws when WebGL2 context creation is unavailable
 */
static checkSupport(): void {

    const canvas = document.createElement('canvas');
    if (!canvas.getContext('webgl2')) {
        throw new Error('GpuDevice requires a WebGL2 context.');
    }
}



/** Detaches the managed canvas from the DOM container. */
[Symbol.dispose](): void {

    if (this.disposed_) return;
    this.disposed_ = true;
    this.div.removeChild(this.canvas);
}


contextLost(event: Event) {

    console.error('WebGL context lost', new Date().toISOString());
    event.preventDefault();
    this.renderer.map.contextLost = true;
    this.bus.emit('gpu-context-lost', {});
};


contextRestored(): void {

    this.bus.emit('gpu-context-restored', {});
};


/**
 * Clamp and store the requested anisotropic filtering level.
 *
 * @param aniso Requested anisotropic filtering level, or -1 for maximum.
 */
setAniso(aniso: GLfloat) {
    if (this.anisoExt) {
        if (this.anisoLevel) {
            if (aniso == -1) {
                this.anisoLevel = this.maxAniso;
            } else {
                this.anisoLevel = Math.min(aniso, this.maxAniso);
            }
        }
    }
};


/**
 * Get the managed canvas element.
 *
 * @returns Canvas element owned by this device.
 */
getCanvas(): HTMLCanvasElement {
    return this.canvas;
};


/**
 * Current drawing destination.
 *
 * `setRenderTarget()` is the only public operation that changes the active
 * target. This getter lets renderer code inspect the target that is already
 * bound without allowing assignment through `gpu.currentRenderTarget = ...`.
 */
get currentRenderTarget(): Readonly<GpuDevice.RenderTarget> {

    return this.renderTarget_;
}


/**
 * Last configured canvas target. This describes the visible map viewport
 * even while an auxiliary framebuffer or texture-space pass is bound.
 */
get canvasRenderTarget(): Readonly<GpuDevice.RenderTarget> {

    return this.canvasTarget_;
}


/**
 * Bind a render target as the active drawing destination.
 *
 * This is the public draw-target switch. It updates `currentRenderTarget`,
 * binds the target framebuffer, and applies the GL viewport. Callers that
 * need to render to the canvas should provide a canvas target built from the
 * renderer's current canvas sizes.
 *
 * @param target Canvas or framebuffer target to draw into.
 */
setRenderTarget(target: GpuDevice.RenderTarget) {

    this.renderTarget_ = target;

    this.bindRenderTargetFramebuffer(target);
    this.resetViewport();
}


private buildCanvasRenderTarget(): GpuDevice.RenderTarget {

    const el = this.div;
    const W = el.offsetWidth || el.clientWidth;
    const H = el.offsetHeight || el.clientHeight;
    const rect = el.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const viewportSize: GpuDevice.NumberPair = [
        Math.round(rect.width * dpr),
        Math.round(rect.height * dpr),
    ];
    const cssLayoutSize: GpuDevice.NumberPair = [W, H];

    return {
        kind: 'canvas',
        viewportSize,
        apparentSize: [rect.width, rect.height],
        cssLayoutSize,
        cssScale: [rect.width / W, rect.height / H],
        devicePixelRatio: dpr,
    };
}


/**
 * Return `true` when the DOM-measured canvas sizes differ from the
 * last value set by `updateCanvasRenderTarget()`. Pure read — no GL
 * state is changed. Call `updateCanvasRenderTarget()` to apply the
 * new sizes.
 */
canvasRenderTargetNeedsUpdate(): boolean {

    const target = this.buildCanvasRenderTarget();
    const cur = this.canvasTarget_;

    return (
        cur.apparentSize[0] !== target.apparentSize[0] ||
        cur.apparentSize[1] !== target.apparentSize[1] ||
        cur.viewportSize[0] !== target.viewportSize[0] ||
        cur.viewportSize[1] !== target.viewportSize[1] ||
        cur.cssLayoutSize?.[0] !== target.cssLayoutSize![0] ||
        cur.cssLayoutSize?.[1] !== target.cssLayoutSize![1] ||
        cur.cssScale?.[0] !== target.cssScale![0] ||
        cur.cssScale?.[1] !== target.cssScale![1] ||
        cur.devicePixelRatio !== target.devicePixelRatio
    );
}


/**
 * Resize the canvas element to the current DOM dimensions, store the
 * result as the cached canvas render target, and bind it as the active
 * render target. Call `Renderer.setProjection()` immediately after.
 *
 * @returns The newly configured canvas render target.
 */
updateCanvasRenderTarget(): GpuDevice.RenderTarget {

    const target = this.buildCanvasRenderTarget();
    const viewportSize = target.viewportSize;
    const cssLayoutSize = target.cssLayoutSize!;

    this.canvas.width = viewportSize[0];
    this.canvas.height = viewportSize[1];
    this.canvas.style.width = cssLayoutSize[0] + 'px';
    this.canvas.style.height = cssLayoutSize[1] + 'px';

    __DEV__ && utils.logOnce(
        `canvas size: [${viewportSize[0]}, ${viewportSize[1]}], `
        + `canvas css size: [${cssLayoutSize[0]} ${cssLayoutSize[1]}]`
    );

    this.canvasTarget_ = target;
    this.setRenderTarget(target);
    return target;
}


/**
 * Bind the cached canvas render target as the active render target.
 * Does not read the DOM or resize the canvas element. Use when
 * switching back to the canvas from an auxiliary pass.
 *
 * @returns The installed canvas render target.
 */
setCanvasRenderTarget(): GpuDevice.RenderTarget {
    this.setRenderTarget(this.canvasTarget_);
    return this.canvasTarget_;
}


/**
 * Build and install a framebuffer render target for an auxiliary pass.
 *
 * Sets the viewport to `viewportSize` and inherits `apparentSize` and the
 * optional CSS size fields from the current canvas render target. Does not
 * call `setProjection()` — auxiliary passes reuse the canvas projection.
 *
 * @param texture Framebuffer-backed texture to draw into.
 * @param viewportSize Texture storage size in physical pixels.
 * @returns The newly installed auxiliary render target.
 */
setAuxiliaryRenderTarget(
    texture: GpuTexture,
    viewportSize: Readonly<GpuDevice.NumberPair>,
): GpuDevice.RenderTarget {

    const base = this.renderTarget_;

    const target: GpuDevice.RenderTarget = {
        kind: 'framebuffer',
        texture,
        viewportSize: [...viewportSize],
        apparentSize: [...base.apparentSize],
    };

    if (base.cssLayoutSize) target.cssLayoutSize = [...base.cssLayoutSize];
    if (base.cssScale)      target.cssScale      = [...base.cssScale];
    if (base.devicePixelRatio !== undefined)
        target.devicePixelRatio = base.devicePixelRatio;

    this.setRenderTarget(target);
    return target;
}


/**
 * Build and install an independent framebuffer render target.
 *
 * Texture-space passes use this for draw targets that do not share the
 * canvas projection or logical size. The target's apparent size is the
 * texture storage size.
 *
 * @param texture Framebuffer-backed texture to draw into.
 * @param viewportSize Texture storage size in physical pixels.
 * @returns The newly installed texture-space render target.
 */
setTextureSpaceRenderTarget(
    texture: GpuTexture,
    viewportSize: Readonly<GpuDevice.NumberPair>,
): GpuDevice.RenderTarget {

    const target: GpuDevice.RenderTarget = {
        kind: 'texture-space',
        texture,
        viewportSize: [...viewportSize],
        apparentSize: [...viewportSize],
    };

    this.setRenderTarget(target);
    return target;
}


/**
 * Restrict drawing inside the current render target.
 *
 * This is used by texture-space blit passes that render into a known
 * sub-rectangle of the already bound target.
 *
 * @param x Left edge in target pixels.
 * @param y Bottom edge in target pixels.
 * @param width Width in target pixels.
 * @param height Height in target pixels.
 */
setViewport(x: number, y: number, width: number, height: number): void {

    this.gl.viewport(x, y, width, height);
}


/**
 * Clear only the active render target's depth buffer.
 */
clearDepth(): void {

    this.gl.depthMask(true);
    this.gl.clear(this.gl.DEPTH_BUFFER_BIT);
    if (!this.currentState.zwrite) this.gl.depthMask(false);
}

/**
 * Clear only the active render target's color buffer.
 *
 * @param color Clear color in 0-255 RGBA components.
 */
clearColor(color: GpuDevice.Color = GpuDevice.Black): void {

    this.setClearColor(color);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
}

/**
 * Clear the active render target's color and depth buffers.
 *
 * @param color Clear color in 0-255 RGBA components.
 */
clearColorAndDepth(color: GpuDevice.Color = GpuDevice.Black): void {

    const gl = this.gl;

    const isIntegerTarget =
        (this.renderTarget_.kind === 'framebuffer'
            || this.renderTarget_.kind === 'texture-space') &&
        this.renderTarget_.texture.usesIntegerColorAttachment();

    if (isIntegerTarget) {

        gl.clearBufferuiv(gl.COLOR, 0, new Uint32Array(color));
        gl.depthMask(true);
        gl.clearBufferfv(gl.DEPTH, 0, new Float32Array([1.0]));
        if (!this.currentState.zwrite) gl.depthMask(false);

    } else {

        this.setClearColor(color);
        gl.depthMask(true);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        if (!this.currentState.zwrite) gl.depthMask(false);
    }
}

/**
 * @deprecated Use `clearDepth`, `clearColor`, or `clearColorAndDepth`.
 *
 * @param clearDepth Whether to clear the depth buffer.
 * @param clearColor Whether to clear the color buffer.
 * @param color Clear color in 0-255 RGBA components.
 */
clear(
    clearDepth: boolean,
    clearColor: boolean,
    color: GpuDevice.Color = GpuDevice.Black,
): void {

    if (clearColor) {
        this.setClearColor(color);
    }

    const clearMask = (clearColor ? this.gl.COLOR_BUFFER_BIT : 0)
        | (clearDepth ? this.gl.DEPTH_BUFFER_BIT : 0);

    if (clearDepth) {

        this.gl.depthMask(true);
        this.gl.clear(clearMask);
        if (!this.currentState.zwrite) this.gl.depthMask(false);

    } else {

        this.gl.clear(clearMask);
    }
};

private setClearColor(color: GpuDevice.Color): void {

    this.gl.clearColor(
        color[0]/255, color[1]/255, color[2]/255, color[3]/255);
}

/**
 * Binds a program without touching vertex attributes or sampler uniforms.
 *
 * The caller is responsible for configuring vertex attributes and sampler
 * uniforms explicitly. New rendering code should use this method instead of
 * `useProgram()`.
 *
 * @param program GPU program object to use.
 */
useProgram2(program: GpuProgram) {

    if (this.currentProgram != program) {

        this.gl.useProgram(program.program);
        this.currentProgram = program;
    }
}

/**
 * Bind a loaded texture to a texture unit.
 *
 * @param texture Texture to bind.
 * @param id Texture unit index. Unit 0 is used when omitted.
 */
bindTexture(texture: GpuTexture, id?: GLint) {

    if (!texture.loaded) 
        throw new Error('Trying to bind a texture that is not loaded.');

    const gl = this.gl;

    const unit = (id == null ? 0 : id);
    this.textureBindCount++;

    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture.texture);

    gl.activeTexture(gl.TEXTURE0);
}

/**
 * Read pixels from a framebuffer-backed texture without changing the
 * tracked render target.
 *
 * This temporarily binds the texture framebuffer as the WebGL read
 * framebuffer, performs `gl.readPixels()`, then restores the read
 * framebuffer that belongs to `currentRenderTarget`. Draw-target changes
 * must still go through `setRenderTarget()`.
 *
 * @param texture Framebuffer-backed texture to read from.
 * @param x Left coordinate in framebuffer pixels.
 * @param y Bottom coordinate in framebuffer pixels.
 * @param lx Width of the read rectangle in pixels.
 * @param ly Height of the read rectangle in pixels.
 * @param data Optional destination buffer. A new buffer is allocated when
 * omitted.
 * @returns Pixel data in RGBA/UNSIGNED_BYTE layout.
 */
readFramebufferPixels(
    texture: GpuTexture,
    x: number,
    y: number,
    lx: number,
    ly: number,
    data?: Uint8Array,
) : Uint8Array {

    if (!texture.framebuffer) {
        throw new Error('Cannot read pixels from a texture without '
            + 'a framebuffer.');
    }

    const gl = this.gl;
    this.bindFramebuffer(texture, gl.READ_FRAMEBUFFER);

    const byteData = data ?? new Uint8Array(lx * ly * 4);

    try {
        gl.readPixels(x, y, lx, ly, texture.readPixelsFormat(),
            gl.UNSIGNED_BYTE, byteData);
    } finally {
        // restore original binding
        this.bindReadFramebufferForRenderTarget(this.renderTarget_);
    }

    return byteData;
}

/**
 * Copy the current render target into a texture of the same format.
 * The destination must already have storage for the copied rectangle.
 * The draw target is unchanged.
 */
copyRenderTargetToTexture(
    destination: GpuTexture,
    width: number,
    height: number,
): void {

    const gl = this.gl;

    this.bindReadFramebufferForRenderTarget(this.renderTarget_);
    gl.bindTexture(gl.TEXTURE_2D, destination.texture);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, width, height);
    gl.bindTexture(gl.TEXTURE_2D, null);
}


/**
 * Start reading a framebuffer-backed texture into `packBuffer` without
 * stalling. Poll the returned fence with `fenceSignalled()`, then take
 * the bytes with `readPixelPackBuffer()`.
 *
 * @param packBuffer destination from `createPixelPackBuffer()`
 * @returns a fence that signals once the read has completed
 */
readFramebufferPixelsAsync(
    texture: GpuTexture,
    width: number,
    height: number,
    packBuffer: WebGLBuffer,
): WebGLSync | null {

    const gl = this.gl;

    this.bindFramebuffer(texture, gl.READ_FRAMEBUFFER);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, packBuffer);

    try {

        gl.readPixels(0, 0, width, height, texture.readPixelsFormat(),
            gl.UNSIGNED_BYTE, 0);

    } finally {

        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
        this.bindReadFramebufferForRenderTarget(this.renderTarget_);
    }

    const fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);

    // A fence signals only after the commands before it have been
    // flushed into the GL command stream.
    gl.flush();

    return fence;
}


/**
 * Allocate a buffer for `readFramebufferPixelsAsync()`. Sized with
 * actual (zeroed) data, not the size-only overload: a driver flags a
 * size-only-allocated buffer's first `readPixels` write as a repeat
 * write with nothing read between them, on every use regardless of how
 * long the previous read has drained, unless the buffer is used for
 * exactly one write in its whole lifetime. Callers that read back more
 * than once must therefore allocate, use, and discard a fresh buffer
 * per read rather than reuse one; see `ElevationUnits.endLookup()`.
 */
createPixelPackBuffer(bytes: number): WebGLBuffer {

    const gl = this.gl;
    const buffer = gl.createBuffer();

    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer);
    gl.bufferData(gl.PIXEL_PACK_BUFFER, new Uint8Array(bytes), gl.STREAM_READ);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);

    return buffer;
}


/**
 * Whether a fence from `readFramebufferPixelsAsync()` has completed.
 * Never waits.
 */
fenceSignalled(fence: WebGLSync): boolean {

    const gl = this.gl;
    const status = gl.clientWaitSync(fence, 0, 0);

    return status === gl.ALREADY_SIGNALED
        || status === gl.CONDITION_SATISFIED;
}


/**
 * Copy a completed read out of its pixel-pack buffer.
 *
 * @param data destination, sized for the read rectangle
 */
readPixelPackBuffer(packBuffer: WebGLBuffer, data: Uint8Array): void {

    const gl = this.gl;

    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, packBuffer);
    gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, data);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
}


private bindRenderTargetFramebuffer(target: GpuDevice.RenderTarget) {

    this.bindFramebuffer(
        target.kind === 'canvas' ? null : target.texture,
        this.gl.FRAMEBUFFER,
    );
}

private resetViewport() {

    this.gl.viewport(
        0,
        0,
        this.renderTarget_.viewportSize[0],
        this.renderTarget_.viewportSize[1],
    );
}

private bindReadFramebufferForRenderTarget(target: GpuDevice.RenderTarget) {

    this.bindFramebuffer(
        target.kind === 'canvas' ? null : target.texture,
        this.gl.READ_FRAMEBUFFER,
    );
}

private bindFramebuffer(texture: GpuTexture | null, target: GLenum) {

    if (texture && !texture.framebuffer) {
        throw new Error('Cannot bind a texture without a framebuffer.');
    }

    const framebuffer = texture ? texture.framebuffer : null;

    // Count a switch only when the bound framebuffer for this target
    // actually changes; redundant re-binds (e.g. per-tile screen target)
    // do not count, matching the external harness.
    if (this.boundFramebuffer_.get(target) !== framebuffer) {

        this.fboSwitchCount++;
        this.boundFramebuffer_.set(target, framebuffer);
    }

    this.gl.bindFramebuffer(target, framebuffer);
};


/**
 * Wrap the context's draw entry points so each draw increments
 * `drawCallCount`. The wrappers defer to the original methods; the cast
 * is needed because native context methods are replaced dynamically.
 */
private installDrawCounters(gl: WebGL2RenderingContext): void {

    const context = gl as unknown as Record<string, (...a: never[]) => unknown>;

    const names = ['drawElements', 'drawArrays',
        'drawElementsInstanced', 'drawArraysInstanced'];

    for (const name of names) {

        const original = context[name].bind(gl);
        context[name] = (...args: never[]) => {

            this.drawCallCount++;
            return original(...args);
        };
    }
};

/**
 * Build a complete fixed-function state from a partial descriptor,
 * filling any unspecified fields with renderer defaults.
 *
 * @param options partial state descriptor
 * @returns complete `GpuDevice.State`
 */
createState(options: Partial<GpuDevice.State>): GpuDevice.State {

    return {
        blend:   options.blend   ?? false,
        stencil: options.stencil ?? false,
        zwrite:  options.zwrite  ?? true,
        ztest:   options.ztest   ?? true,
        zequal:  options.zequal  ?? false,
        culling: options.culling ?? true,
    };
};


/**
 * Apply WebGL fixed-function state changes.
 *
 * @param state Desired fixed-function state. Missing/null state is ignored
 * for legacy callers.
 * @param force Apply every field even when the cache already matches.
 */
setState(state: GpuDevice.State, force = false) {

    if (!state) {
        return;
    }

    var gl = this.gl;
    var currentState = this.currentState;

    if (force || currentState.blend != state.blend) {
        if (state.blend) {
            gl.blendEquationSeparate(gl.FUNC_ADD, gl.FUNC_ADD);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            gl.enable(gl.BLEND);
        } else {
            gl.disable(gl.BLEND);
        }
    }

    if (force || currentState.stencil != state.stencil) {
        if (state.stencil) {
            gl.enable(gl.STENCIL_TEST);
        } else {
            gl.disable(gl.STENCIL_TEST);
        }
    }

    if (force || currentState.zwrite != state.zwrite) {
        if (state.zwrite) {
            gl.depthMask(true);
        } else {
            gl.depthMask(false);
        }
    }

    if (force || currentState.ztest != state.ztest) {
        if (state.ztest) {
            gl.enable(gl.DEPTH_TEST);
        } else {
            gl.disable(gl.DEPTH_TEST);
        }
    }

    if (force || currentState.zequal != state.zequal) {
        if (state.zequal) {
            gl.depthFunc(gl.LEQUAL);
        } else {
            gl.depthFunc(gl.LESS);
        }
    }

    if (force || currentState.culling != state.culling) {
        if (state.culling) {
            gl.enable(gl.CULL_FACE);
        } else {
            gl.disable(gl.CULL_FACE);
        }
    }

    this.currentState = state;
};

/**
 * @deprecated Legacy program binding API. It silently assigns sampler
 * uniforms and toggles global vertex-attribute enable state. New code should
 * use `useProgram2()` and configure attributes/samplers explicitly.
 *
 * @param program GPU program object to use.
 * @param attributes Attribute names to enable for the bound program.
 * @param nextSampler Whether to bind legacy `uSampler2` to texture unit 1.
 */
useProgram(program: GpuProgram, attributes: string[], nextSampler: boolean) {

    if (this.currentProgram != program) {

        this.gl.useProgram(program.program);
        this.currentProgram = program;

        // why this is done for every program statically i do not know
        // in the tile program the first uniform identifies the main
        // texture slot (0), the second one the mask (1)
        program.setSampler('uSampler', 0);

        if (nextSampler) {
            program.setSampler('uSampler2', 1);
        }

        // Legacy path only: newer mesh rendering calls useProgram2(), then
        // GpuMesh.draw2() binds attributes through a VAO built from the
        // attribute names passed by the caller.
        var newAttributes = this.newAttributes;
        var enabledAttributes = this.enabledAttributes;

        //reset new attributes list
        for (var i = 0, li = newAttributes.length; i < li; i++){
            newAttributes[i] = 0;
        }

        for (i = 0, li = attributes.length; i < li; i++){
            var index = program.getAttribLocation(attributes[i]);

            if (index != -1){
                newAttributes[index] = 1;
            }
        }

        //enable or disable current attributes according to new attributes list
        for (i = 0, li = newAttributes.length; i < li; i++){
            if (enabledAttributes[i] != newAttributes[i]) {
                if (newAttributes[i]) {
                    this.gl.enableVertexAttribArray(i);
                    enabledAttributes[i] = 1;
                } else {
                    this.gl.disableVertexAttribArray(i);
                    enabledAttributes[i] = 0;
                }
            }
        }
    }
};

} // class GpuDevice

// exported types
export namespace GpuDevice {

/**
 * Two-number tuple used for dimensions and scale factors.
 */
export type NumberPair = [number, number];

/**
 * Clear color in 0-255 RGBA components.
 */
export type Color = readonly [number, number, number, number];

/**
 * Opaque black clear color.
 */
export const Black: Color = [0, 0, 0, 255];

/**
 * Opaque white clear color.
 */
export const White: Color = [255, 255, 255, 255];

/**
 * Cached WebGL fixed-function state managed by `GpuDevice.setState()`.
 */
export type State = {
    blend: boolean,
    stencil: boolean,
    zequal: boolean,
    ztest: boolean,
    zwrite: boolean,
    culling: boolean
}

/**
 * Shared size fields for every drawing destination tracked by `GpuDevice`.
 */
export type RenderTargetBase = {

    /**
     * GL viewport/backing-store size in physical pixels, passed to
     * `gl.viewport()`.
     *
     * For the canvas target this is the physical backing size
     * (`canvas.width`, `canvas.height`). For framebuffer targets this is
     * the texture/framebuffer storage size.
     */
    viewportSize: NumberPair,

    /**
     * Apparent logical size: the visible extent of the target in CSS units
     * after any CSS transforms. Equal to `cssLayoutSize * cssScale`.
     *
     * This is the size used for projection matrices, camera aspect, and
     * screen-space draw helpers. For the canvas target it equals
     * `getBoundingClientRect()` dimensions. For auxiliary targets it is
     * inherited from the canvas target.
     */
    apparentSize: NumberPair,

    /**
     * Pre-transform CSS layout size in CSS pixels (`offsetWidth` /
     * `offsetHeight`). Mouse event coordinates are reported in this space.
     * Not present on independent render targets.
     */
    cssLayoutSize?: NumberPair,

    /**
     * Axis-aligned CSS transform scale: `apparentSize / cssLayoutSize`.
     * A reveal-style `scale(0.5)` gives `[0.5, 0.5]`.
     * Not present on independent render targets.
     */
    cssScale?: NumberPair,

    /**
     * Device pixel ratio at the time the canvas target was built.
     * Not present on independent render targets.
     */
    devicePixelRatio?: number,
}

/**
 * Drawing destination backed by the default canvas framebuffer.
 */
export type CanvasTarget = RenderTargetBase & {
    kind: 'canvas'
}

/**
 * Drawing destination backed by a texture framebuffer.
 */
export type FramebufferTarget = RenderTargetBase & {

    /**
     * Texture whose framebuffer is bound when this target is active.
     */
    texture: GpuTexture,

    kind: 'framebuffer'
}

/**
 * Drawing destination backed by an independent texture framebuffer.
 */
export type TextureSpaceTarget = RenderTargetBase & {

    /**
     * Texture whose framebuffer is bound when this target is active.
     */
    texture: GpuTexture,

    kind: 'texture-space'
}

/**
 * Active drawing destination tracked by `GpuDevice`.
 */
export type RenderTarget = CanvasTarget | FramebufferTarget | TextureSpaceTarget

} // export namespace GpuDevice


export default GpuDevice;
